import {
  CategoryNameSchemasType,
  DataObjectGroupProcessingStateType,
  DataRowType,
  filterString,
  formatCodeName,
  getLatestOutputVersionId,
  getDataObjectVersions,
  SubjectsType,
  ISODate,
  getGroupDescriptionRef,
  getDayjs,
  getDayjsNow,
  getDuration,
  ISO_DATE_FORMAT,
  useQueryParams,
} from '@atlas-frontend/sdk';
import { Row } from '@tanstack/react-table';
import { path } from '../../paths';
import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import UriTemplate from 'uri-template-lite';

export function getRowIdAndType(row: DataObjectGroupProcessingStateType) {
  const {
    standaloneDataObjectDescriptionId,
    dataObjectGroupId,
    dataObjectGroupCode,
    dataObjectGroupName,
    valueDate,
  } = row;
  let id = standaloneDataObjectDescriptionId;
  let type: 'description' | 'group' = 'description';
  if (!standaloneDataObjectDescriptionId) {
    id = dataObjectGroupId;
    type = 'group';
  }

  const name = formatCodeName({
    code: dataObjectGroupCode,
    name: dataObjectGroupName,
  });

  return { id, name, type, valueDate };
}

export function makeInputDataFilterFn(
  nameMapping?: CategoryNameSchemasType | SubjectsType | null,
) {
  return (row: Row<DataRowType>, columnId: string, filter: any) => {
    if (!filter) {
      return true;
    }

    if (row.original.isNew) {
      return true;
    }

    const code: string = row.renderValue(columnId)?.toString() ?? '';
    let matches = filterString(code, filter);
    if (!matches) {
      const name = nameMapping?.[code];
      if (name) {
        matches = filterString(
          typeof name === 'object' ? name.name : name,
          filter,
        );
      }
    }

    return matches;
  };
}

export const useNavigateToRelationsWithLatestVersion = (
  outputType: string,
  valueDate: ISODate,
  groupId: number,
  descriptionId: number | null,
) => {
  const queryClient = useQueryClient();

  const navigateToRelationsWithLatestVersion = useCallback(async () => {
    const baseParams = {
      type: outputType,
      groupOrDescriptionId: getGroupDescriptionRef(groupId, descriptionId),
      showOneStep: 'true',
      valueDate: valueDate,
      showIntermediates: 'false',
    };

    let versionParam: string | number | undefined = 'latest';

    try {
      if (descriptionId) {
        const id = descriptionId;

        const versions = await queryClient.fetchQuery({
          queryKey: ['data-object-relation-versions', { id, valueDate }],
          queryFn: () => getDataObjectVersions({ id, valueDate: valueDate }),
        });

        versionParam = getLatestOutputVersionId(versions);
      }
    } catch (_) {
      versionParam = undefined;
    }

    const url = new UriTemplate(
      `${path.relations}{?type,groupOrDescriptionId,showOneStep,valueDate,version,showIntermediates}`,
    ).expand({ ...baseParams, version: versionParam });

    window.open(url, '_blank');
  }, [outputType, valueDate, queryClient, groupId, descriptionId]);

  return navigateToRelationsWithLatestVersion;
};

export function getPeriodToDays(period?: [string, string]): number {
  if (period) {
    const startDate = getDayjs(period[0]);
    const endDate = getDayjs(period[1]);
    return startDate && endDate
      ? Number(getDuration(endDate.diff(startDate)).asDays().toFixed(0))
      : 0;
  }
  return 0;
}

export function getDaysToPeriod(
  valueDays?: number,
): [string, string] | undefined {
  if (valueDays) {
    const endValidDate = getDayjsNow().subtract(1, 'day');
    const startValidDate = endValidDate.subtract(valueDays, 'day');
    return [
      startValidDate.format(ISO_DATE_FORMAT),
      endValidDate.format(ISO_DATE_FORMAT),
    ];
  }
  return undefined;
}

export const useDaysToSetQueryParamsPeriod = () => {
  const [queryParams, setQueryParams] = useQueryParams();
  const { valueDate, valueDays } = queryParams as {
    valueDays?: number;
    valueDate?: [string, string];
  };
  useEffect(() => {
    let daysValueDate = valueDate;
    if (!valueDate && valueDays) {
      daysValueDate = getDaysToPeriod(valueDays);
      setQueryParams?.(
        { valueDays: undefined, valueDate: daysValueDate?.toString() },
        { replace: true },
      );
    }
  }, [setQueryParams, valueDate, valueDays]);
};
